// src/ui/screens/post/widgets/ListingItem.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../hooks/useAuth';
import { useLike } from '../../../../utils/likeProvider';
import { useSave } from '../../../../utils/saveProvider';
// FIX: UserAvatar is a default export (confirmed by SettingsScreen.jsx's
// `import UserAvatar from '../../../components/UserAvatar'`). This file was
// importing it as a named export, which resolves to `undefined` and crashes
// the whole card the moment React tries to render it — very likely the real
// cause of the "missing action buttons" symptom, since the crash takes out
// everything below it in the tree, not just the avatar.
import UserAvatar from '../../../../components/UserAvatar';
import { db } from '../../../../firebase';
import { doc, getDoc, updateDoc, increment, onSnapshot } from 'firebase/firestore';
// FIX: swapped the hand-rolled inline SVGs on the action bar for the same
// lucide-react icons VideoItem.jsx uses (Heart / MessageCircle / Bookmark /
// Send), so the feed card and the Atom video player render identical icons.
import { Heart, MessageCircle, Bookmark, Send } from 'lucide-react';
// FIX: this file used to render a placeholder `CommentsModal` stub because
// the real comment list/composer wasn't available. It lives right next to
// this file (see VideoItem.jsx's own import: '../../ui/screens/post/
// widgets/CommentSection', which resolves to this same directory), so we
// can now open the real thing — matching exactly how VideoItem opens it.
import { CommentSection } from './CommentSection';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  pink: '#FF1493',
  blue: '#4DA3FF',
};

// ── Media Types ──────────────────────────────────────────────────────────────

const MediaType = {
  VIDEO_COVER: 'videoCover',
  IMAGE: 'image',
  PLACEHOLDER: 'placeholder',
};

// ── ShimmerBox Component ─────────────────────────────────────────────────────

const ShimmerBox = () => {
  const [opacity, setOpacity] = useState(0.04);

  useEffect(() => {
    let direction = 1;
    const interval = setInterval(() => {
      setOpacity((prev) => {
        let newVal = prev + direction * 0.004;
        if (newVal >= 0.12) {
          direction = -1;
          newVal = 0.12;
        } else if (newVal <= 0.04) {
          direction = 1;
          newVal = 0.04;
        }
        return newVal;
      });
    }, 16);

    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="w-full h-full"
      style={{ backgroundColor: COLORS.card }}
    >
      <div
        className="w-full h-full transition-all duration-200"
        style={{ backgroundColor: `rgba(255, 255, 255, ${opacity})` }}
      />
    </div>
  );
};

// ── ActionButton Component ──────────────────────────────────────────────────
// FIX: previously picked between four inline <path> blocks by an `icon`
// string. Now maps that same string to a lucide-react component, matching
// VideoItem's SideButton — including the same fill-when-active behavior
// (favorite/bookmark visually "fill" in when toggled on, chat/share never
// do since they're never in an active state here).

const ACTION_ICONS = {
  favorite: Heart,
  chat: MessageCircle,
  bookmark: Bookmark,
  share: Send,
};

const ActionButton = ({ icon, activeColor, isActive, onTap }) => {
  const IconComponent = ACTION_ICONS[icon];
  if (!IconComponent) return null;

  return (
    <button
      onClick={onTap}
      className="flex items-center justify-center p-1.5 hover:opacity-80 transition-opacity"
    >
      <IconComponent
        size={18}
        color={isActive ? activeColor : 'rgba(255,255,255,0.55)'}
        fill={isActive ? activeColor : 'none'}
      />
    </button>
  );
};

// ── ListingItem Component ────────────────────────────────────────────────────

export const ListingItem = ({ listing }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isLiked, toggleLike } = useLike();
  const { isSaved, toggleSave } = useSave();

  const [userData, setUserData] = useState({
    displayName: 'Unknown',
    profileImage: '',
    verified: false,
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [mediaItems, setMediaItems] = useState([]);
  const [isSharing, setIsSharing] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const pageContainerRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const listingId = listing.id || '';
  const ownerUid = listing.uid || listing.userRef || '';
  const listingTitle = listing.title || 'your listing';
  const thumbnailUrl = listing.thumbnailUrl || '';
  const firstImageUrl = listing.imageUrls?.[0] || null;

  // ── Build media items ──────────────────────────────────────────────────────

  useEffect(() => {
    const items = [];
    const hasVideo = listing.hasVideo || (listing.videoUrl && listing.videoUrl.length > 0);

    if (hasVideo) {
      const thumbUrl = listing.thumbnailUrl || '';
      const firstPhoto = listing.imageUrls?.find(u => u) || '';
      const coverUrl = thumbUrl || firstPhoto || '';
      items.push({ type: MediaType.VIDEO_COVER, url: coverUrl });
    }

    const imageUrls = listing.imageUrls || [];
    const hasVideo2 = listing.hasVideo || (listing.videoUrl && listing.videoUrl.length > 0);
    const videoUsedFirstPhoto = hasVideo2 && !listing.thumbnailUrl && imageUrls.length > 0;
    const photoStart = videoUsedFirstPhoto ? 1 : 0;

    for (let i = photoStart; i < imageUrls.length; i++) {
      if (imageUrls[i]) {
        items.push({ type: MediaType.IMAGE, url: imageUrls[i] });
      }
    }

    if (items.length === 0) {
      items.push({ type: MediaType.PLACEHOLDER, url: '' });
    }

    setMediaItems(items);
  }, [listing]);

  // ── Fetch user data ──────────────────────────────────────────────────────
  // FIX: `verified` on the user doc mirrors the same field-name bug that was
  // already fixed elsewhere (isVerified vs verified) — reading `isVerified`
  // first, falling back to `verified` for older docs, matching the Dart fix.

  useEffect(() => {
    if (!ownerUid) return;

    const unsubscribe = onSnapshot(doc(db, 'users', ownerUid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUserData({
          displayName: data.displayName || data.name || 'Unknown',
          profileImage: data.profileImage || data.photoURL || '',
          verified: data.isVerified === true || data.verified === true,
        });
      }
    }, (error) => {
      console.error('ListingItem user fetch error:', error);
    });

    return () => unsubscribe();
  }, [ownerUid]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const getPriceText = () => {
    const price = listing.price;
    const isNegotiable = listing.isNegotiable;
    if (isNegotiable || !price || price === 'Negotiable') {
      return 'Negotiable';
    }
    if (typeof price === 'number') {
      return `UGX ${price.toLocaleString()}`;
    }
    return price.toString();
  };

  const getLocationText = () => {
    const parts = [];
    if (listing.district) parts.push(listing.district);
    if (listing.division) parts.push(listing.division);
    return parts.join(', ');
  };

  const getCategory = () => {
    return listing.subcategory || listing.category || '';
  };

  const handleOpenListing = async () => {
    // Guard against a missing/empty id navigating to a malformed URL
    // (e.g. "/listing/" or "/listing/undefined"), which can itself present
    // as a blank screen since it won't cleanly match "/listing/:id".
    if (!listingId) {
      console.error('ListingItem: cannot open listing — missing id on', listing);
      return;
    }
    try {
      await updateDoc(doc(db, 'listings', listingId), {
        viewCount: increment(1),
      });
    } catch (error) {
      console.error('View count update error:', error);
    }
    navigate(`/listing/${listingId}`);
  };

  const handleOpenOwnerProfile = () => {
    // NOTE: this already correctly targets the listing owner's uid, not the
    // current user. If this still opens the logged-in user's own profile,
    // the bug is in ProfileScreen.jsx — most likely it isn't reading the
    // `:userId` route param and is defaulting to the authed user regardless
    // of the URL. Share that file to confirm/fix.
    if (ownerUid) navigate(`/profile/${ownerUid}`);
  };

  const handleLike = async () => {
    if (!user) {
      // Show snackbar or toast
      return;
    }
    if (!listingId) return;

    await toggleLike({
      listingId,
      listingOwnerId: ownerUid,
      listingTitle,
      thumbnailUrl: thumbnailUrl || firstImageUrl,
      listingImageUrl: firstImageUrl,
    });
  };

  const handleSave = async () => {
    if (!user) return;
    if (!listingId) return;

    await toggleSave({
      listingId,
      listingOwnerId: ownerUid,
      listingTitle,
      thumbnailUrl: thumbnailUrl || firstImageUrl,
      listingImageUrl: firstImageUrl,
    });
  };

  // FIX: now opens the real CommentSection component inline, exactly the
  // way VideoItem.jsx does (`setShowCommentSection(true)` +
  // `<CommentSection listingId={...} onClose={...} />`), instead of
  // navigating to a route that was never registered in App.js.
  const handleShowComments = () => {
    if (!listingId) return;
    setShowComments(true);
  };

  const handleShare = async () => {
    if (!listingId) return;
    setIsSharing(true);
    try {
      const title = listing.title || 'this property';
      const url = `https://filatom.com/listing/${listingId}`;
      if (navigator.share) {
        await navigator.share({
          title: `Check out "${title}" on Filatom!`,
          text: `Check out "${title}" on Filatom!`,
          url: url,
        });
      } else {
        await navigator.clipboard.writeText(`Check out "${title}" on Filatom!\n${url}`);
        // Show toast
      }
    } catch (error) {
      console.error('Share error:', error);
    } finally {
      setIsSharing(false);
    }
  };

  // ── Touch handlers for carousel ─────────────────────────────────────────

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentPage < mediaItems.length - 1) {
        setCurrentPage(currentPage + 1);
      } else if (diff < 0 && currentPage > 0) {
        setCurrentPage(currentPage - 1);
      }
    }
  };

  // ── Render helpers ──────────────────────────────────────────────────────

  const renderMedia = () => {
    const item = mediaItems[currentPage] || mediaItems[0];

    if (!item) return null;

    const renderContent = () => {
      if (item.type === MediaType.VIDEO_COVER) {
        if (!item.url) {
          return (
            <div className="w-full h-full flex flex-col items-center justify-center" style={{ backgroundColor: COLORS.card }}>
              <svg className="w-9 h-9" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24" style={{ opacity: 0.4 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="text-white/25 text-xs mt-1 font-medium">Video</span>
            </div>
          );
        }
        return (
          <img
            src={item.url}
            alt="Video cover"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        );
      }

      if (item.type === MediaType.IMAGE) {
        return (
          <img
            src={item.url}
            alt="Listing"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        );
      }

      return (
        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: COLORS.card }}>
          <svg className="w-12 h-12 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1" />
          </svg>
        </div>
      );
    };

    return (
      <div
        className="relative w-full h-[200px] overflow-hidden cursor-pointer"
        onClick={handleOpenListing}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        ref={pageContainerRef}
      >
        {renderContent()}
      </div>
    );
  };

  const renderCategoryBadge = () => {
    const category = getCategory();
    if (!category) return null;

    const label = category.length > 18 ? category.substring(0, 16) + '…' : category;

    return (
      <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
        <span className="text-white/80 text-[9px] font-semibold tracking-wide truncate max-w-[90px] block">
          {label}
        </span>
      </div>
    );
  };

  const renderCarouselIndicators = () => {
    if (mediaItems.length <= 1) return null;

    return (
      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
        {mediaItems.map((_, i) => (
          <div
            key={i}
            className={`h-1 rounded-full transition-all duration-200 ${
              i === currentPage ? 'w-3.5 bg-filatom-primary' : 'w-1.5 bg-white/45'
            }`}
          />
        ))}
      </div>
    );
  };

  const renderPageCounter = () => {
    if (mediaItems.length <= 1) return null;

    return (
      <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
        <span className="text-white/80 text-[9px] font-semibold">
          {currentPage + 1}/{mediaItems.length}
        </span>
      </div>
    );
  };

  const renderVideoBadge = () => {
    const item = mediaItems[currentPage];
    if (!item || item.type !== MediaType.VIDEO_COVER) return null;

    return (
      <>
        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded flex items-center gap-0.5" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <svg className="w-2.5 h-2.5" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <span className="text-filatom-primary text-[9px] font-bold">Atom</span>
        </div>
        <div className="absolute bottom-6 right-2 w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </>
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────

  const isLikedState = isLiked(listingId);
  const isSavedState = isSaved(listingId);
  const locationText = getLocationText();
  const priceText = getPriceText();

  return (
    <div
      className="rounded-xl overflow-hidden border border-white/5"
      style={{ backgroundColor: COLORS.bg }}
    >
      {/* Media Carousel */}
      <div className="relative">
        {renderMedia()}
        {renderCategoryBadge()}
        {renderVideoBadge()}
        {renderCarouselIndicators()}
        {renderPageCounter()}
      </div>

      {/* Content */}
      <div className="px-2.5 pt-2.5 pb-2">
        <div onClick={handleOpenListing}>
          <h3 className="text-white font-semibold text-sm truncate">
            {listing.title || 'No title'}
          </h3>
        </div>

        {/* Owner Row */}
        <div onClick={handleOpenOwnerProfile} className="flex items-center gap-2 mt-2 cursor-pointer">
          <UserAvatar size={28} imageUrl={userData.profileImage || null} name={userData.displayName} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-0.5">
              <span className="text-white/65 text-xs font-medium truncate">
                {userData.displayName}
              </span>
              {userData.verified && (
                <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" style={{ color: COLORS.blue }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              )}
            </div>
            <span className="text-filatom-primary text-[10px] font-bold">{priceText}</span>
          </div>
          <svg className="w-3.5 h-3.5 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>

        {/* Location */}
        {locationText && (
          <div className="flex items-center gap-1 mt-0.5">
            <svg className="w-3 h-3 text-white/40" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
            <span className="text-white/55 text-xs truncate">{locationText}</span>
          </div>
        )}

        {/* Action Bar */}
        <div className="flex justify-evenly mt-1">
          <ActionButton
            icon="favorite"
            activeColor={COLORS.pink}
            isActive={isLikedState}
            onTap={handleLike}
          />
          <ActionButton
            icon="chat"
            activeColor={COLORS.orange}
            isActive={false}
            onTap={handleShowComments}
          />
          <ActionButton
            icon="bookmark"
            activeColor={COLORS.blue}
            isActive={isSavedState}
            onTap={handleSave}
          />
          {isSharing ? (
            <div className="flex items-center justify-center p-1.5">
              <div className="w-4 h-4 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <ActionButton
              icon="share"
              activeColor="#FFFFFF"
              isActive={false}
              onTap={handleShare}
            />
          )}
        </div>
      </div>

      {showComments && listingId && (
        <CommentSection listingId={listingId} onClose={() => setShowComments(false)} />
      )}
    </div>
  );
};

export default ListingItem;